<template>
	<div class="page-my contents">
		<div class="main-content">
			<ClientOnly>
				<!-- 用户信息 -->
				<div class="user-info">
					<div class="left">
						<div class="user-avatar">
							<img class="avatar" src="/staticSeo/images/my-user-avatar.png" mode="" />
						</div>
						<div class="basic-info" v-if="isLogin">
							<div class="company">{{ formatUserName }}</div>
							<div class="name">账号：{{ userInfo.userName }}</div>
						</div>
						<div class="login-register" v-else @click="login">请登录/注册</div>
					</div>
					<div class="right" v-if="isLogin">
						<div class="nav-item" v-for="(item, idx) in topNav" :key="idx" @click="goToPage(item)">
							<span :class="['iconfont', item.icon]">
								<text v-if="item.badge && item.badge.show" :class="['badge', item.badge.type]">
									{{ item.badge.text }}
								</text>
							</span>
							<text class="name">{{ item.name }}</text>
						</div>
					</div>
				</div>
				<!-- 快捷入口 -->
				<div class="quick-entry-box">
					<div class="entry-item" v-for="(item, idx) in quickEntry" :key="idx" @click="goToPage(item)">
						<img :src="item.img" />
						<text class="entry-name">{{ item.name }}</text>
					</div>
				</div>
				<!-- 我的订单 -->
				<FunctionsCard :cardData="myOrder" @goToPage="goToPage" />
				<!-- 我的资产 -->
				<FunctionsCard :cardData="myAssets" @goToPage="goToPage" />
				<!-- 其他功能 -->
				<FunctionsCard :cardData="moreServices" @goToPage="goToPage" />
				<!-- 猜你喜欢 -->
				<GuessLike v-if="recommendList.length > 0" :dataList="recommendList" fromPage="个人中心" @click="clickGuessLike" />
				<!-- 底部 TabBar -->
				<footerBar :checks="4" />
			</ClientOnly>
		</div>
	</div>
</template>

<script setup>
import { getMyCoupon, recommendProducts } from '@/api/index.js';
import { getRebateUseBlance, queryTitleAndNumber } from '@/api/my.js';
import { IMPLANT_REPORT } from '@/constant/implanReport';
import { ownListDemand } from '@/api/order.js';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import FunctionsCard from './components/FunctionsCard.vue';
import GuessLike from '@/components/GuessLike.vue';
import { useAnalytics } from '@/composables/useAnalytics';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getStorageSync, removeStorageSync } from '@/utils/storage.js';
useSeoMeta({
	title: '国械在线-我的',
	ogTitle: '国械在线-我的',
	ogImage: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
});

const route = useRoute();
const authStore = useAuthStore();
const userStore = useUserStore();
const { trackEvent, trackEventExposure, disconnectAllExposure } = useAnalytics();
const isLogin = ref(false);
const unpaidOrderCount = ref(0);
const blanceAmount = ref(0);
const couponNumber = ref(0);
const recommendList = ref([]);
const noticeList = ref({});
const userInfo = ref({
	customerCompanyName: '',
	userName: '',
	userId: null,
});

const getMergedUserInfo = () => {
	const storedUserInfo = getStorageSync('userInfo');
	const storedUser = getStorageSync('user');
	const loginInfo = userStore.loginInfo;

	return {
		customerCompanyName:
			storedUserInfo?.customerCompanyName || storedUser?.customerCompanyName || loginInfo?.companyName || loginInfo?.realName || '',
		userName: storedUserInfo?.userName || storedUser?.userName || loginInfo?.user?.name || loginInfo?.realName || '',
		userId: storedUserInfo?.userId || storedUser?.userId || loginInfo?.userId || null,
	};
};

const syncUserState = () => {
	isLogin.value = authStore.isLoggedIn || !!getStorageSync('cmdc_access_token_mobile');
	userInfo.value = getMergedUserInfo();
};

const quickEntry = ref([
	{
		img: '/staticSeo/images/my-product.png',
		name: '常购清单',
		url: '/frequentlyPurchasedProduct',
		checkLogin: true,
	},
	{ img: '/staticSeo/images/my-collectProduct.png', name: '收藏商品', url: '/favoriteProduct', checkLogin: true },
	{ img: '/staticSeo/images/my-shop.png', name: '关注店铺', url: '/followStore', checkLogin: true },
	{ img: '/staticSeo/images/my-viewHistory.png', name: '浏览历史', url: '/browserHistory', checkLogin: true },
]);

const myAssets = ref({
	title: '我的资产',
	titleRight: null,
	list: [
		{ icon: 'icon-wodefanli', name: '我的返利', url: '/pages/rebate/rebate', checkLogin: true },
		{ icon: 'icon-wodejifen', name: '我的积分', url: '/pages/my/prize', checkLogin: true },
	],
});

const goInvoiceImplantationReporting = () => {
	removeStorageSync(IMPLANT_REPORT.IMPLANT_REPORT_LIST_ITEM);
	removeStorageSync(IMPLANT_REPORT.IMPLANT_REPORT_GLOBAL);
};

const moreServices = ref({
	title: '其他功能',
	titleRight: null,
	list: [
		{ icon: 'icon-hezuoshouying', name: '合作首营', url: '/pages/cooperationBusiness/cooperationBusiness', checkLogin: true },
		{ icon: 'icon-dianzifapiaoguanli', name: '电子发票管理', url: '/pages/invoiceMgt/invoiceMgt', checkLogin: true },
		{
			icon: 'icon-zhirushangbao',
			name: '植入上报',
			url: '/pages/invoiceImplantationReporting/list',
			func: goInvoiceImplantationReporting,
			checkLogin: true,
		},
		{
			icon: 'icon-fapiaoshangbao',
			name: '发票上报',
			url: '/pages/invoiceImplantationReporting/list',
			func: goInvoiceImplantationReporting,
			checkLogin: true,
		},
	],
});

const topNav = computed(() => {
	const hasUnread = Object.keys(noticeList.value).some(key => noticeList.value[key] && noticeList.value[key] != 0);
	return [
		{
			icon: 'icon-v2_xiaoxi',
			name: '消息',
			url: '/pages/myMessages/index',
			checkLogin: true,
			badge: {
				show: hasUnread,
				type: 'danger',
				text: '',
			},
		},
		{ icon: 'icon-v2_kefu', name: '客服', url: '/pages/kf/contactKF', checkLogin: false },
		{ icon: 'icon-v2_shezhi', name: '设置', url: '/my/set', checkLogin: true },
	];
});

const myOrder = computed(() => {
	return {
		title: '我的订单',
		titleRight: {
			text: '全部',
			icon: 'icon-maijia_youjin',
			type: 'expand',
			url: '/pages/myOrder/orderList',
			checkLogin: true,
		},
		list: [
			{ icon: 'icon-shenhezhong', name: '审核中', url: '/pages/myOrder/orderList?type=1', checkLogin: true },
			{
				icon: 'icon-daifukuan',
				name: '待付款',
				url: '/pages/myOrder/orderList?type=2',
				checkLogin: true,
				badge: {
					show: unpaidOrderCount.value !== 0,
					type: 'danger',
					text: unpaidOrderCount.value,
				},
			},
			{ icon: 'icon-daishouhuo', name: '待收货', url: '/pages/myOrder/orderList?type=3', checkLogin: true },
			{ icon: 'icon-yiwancheng', name: '已完成', url: '/pages/myOrder/orderList?type=5', checkLogin: true },
		],
	};
});

const formatUserName = computed(() => {
	if (userInfo.value) {
		let userName = userInfo.value.customerCompanyName || '';
		if (userName && userName.length > 10) {
			return userName.substring(0, 4) + '****' + userName.substring(userName.length - 4);
		}
		return userName;
	}
	return '';
});

const getRecommendProducts = async () => {
	try {
		const params = {
			type: 1,
			companyCode: '',
			currentProductList: [],
			limit: 18,
		};
		let { code, data } = await recommendProducts(params);
		if (code == 200) {
			recommendList.value = data || [];
			await nextTick();
			// 监听猜你喜欢曝光
			trackEventExposure(
				'.guess-like',
				{
					module: '个人中心',
					eventCode: 'm-user_center_module_exposure',
					eventName: '个人中心模块曝光',
					attrCode: 'module_name',
					attrName: '猜你喜欢',
				},
				{ threshold: [0.2], rootMargin: '0px' }
			);
		}
	} catch (e) {
		console.log(e);
	}
};

const getNoticeCount = async () => {
	try {
		let { code, data } = await queryTitleAndNumber({
			pageNumber: 1,
			pageSize: 10,
		});
		if (code == 200) {
			noticeList.value = data;
		}
	} catch (e) {
		console.log(e);
	}
};

const login = () => {
	navigateTo({
		path: '/login',
	});
};

const goToPage = item => {
	const { url, func, checkLogin } = item;
	if (!isLogin.value && checkLogin) {
		login();
		return;
	}
	if (func) {
		func();
	}
	const { $navigateToOld } = useNuxtApp();
	$navigateToOld({ path: url });
};

const getBaseInfo = async () => {
	try {
		let params = {
			pageSize: 1,
			pageStart: 1,
			listOrderStatus: [1021, 132],
			sellerCompanyCodes: [],
		};

		let { code, data } = await ownListDemand(params);
		if (code == 200) {
			unpaidOrderCount.value = data.total > 99 ? '99+' : data.total;
		}
	} catch (e) {
		console.log(e);
	}
	const userId = userInfo.value?.userId;
	if (!userId) return;

	getRebateUseBlance(userId).then(res => {
		if (res.code == 200) {
			blanceAmount.value = res.data;
		}
	});
	getMyCoupon({
		status: 101,
	}).then(res => {
		if (res.code == 200) {
			couponNumber.value = res.data.length;
		}
	});
};

const clickGuessLike = productInfo => {
	trackEvent({
		companyCode: productInfo.companyCode,
		companyName: productInfo.companyName,
		module: '个人中心',
		eventCode: 'user_center__click',
		eventName: '个人中心点击',
		attrCode: 'button_name',
		attrName: '猜你喜欢',
		ext1: productInfo.productCode,
		ext2: productInfo.productName,
	});
};

// onShow(() => {

// });

onMounted(() => {
	syncUserState();

	if (isLogin.value) {
		getBaseInfo();
		getNoticeCount();
	} else {
		unpaidOrderCount.value = 0;
	}
	getRecommendProducts();
});

watch(
	() => authStore.isLoggedIn,
	() => {
		syncUserState();
	}
);
onUnmounted(() => {
	disconnectAllExposure();
});
</script>

<style lang="scss" scoped>
:deep(.mini-list) {
	padding: 8px 0 0 0 !important;
}
:deep(.guess-like) {
	margin-top: 8px !important;
	padding-top: 8px !important;
	padding-bottom: calc(env(safe-area-inset-bottom) + 4.8rem);
}
.page-my {
	height: 100vh;
	min-height: 100vh;
	max-height: 100vh;
}
.main-content {
	height: 100vh;
	min-height: 100vh;
	max-height: 100vh;
	/* height: 100%; */
	width: 100%;
	max-height: 100%;
	position: relative;
	padding: 0 8px;
	background: linear-gradient(180deg, #4d67d3 0%, #516cde 12%, #ebeefa 23%);
	overflow-y: auto;
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 92px;
		background: url('/staticSeo/images/my-page-bg.png') no-repeat left top;
		background-size: 100% 100%;
	}
}

.user-info {
	height: 88px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 8px 20px;
	color: #fff;
	position: relative;
	z-index: 1;
	.left,
	.right {
		display: flex;
		align-items: center;
	}

	.user-avatar {
		width: 44px;
		height: 44px;
		border: 1px solid #ffffff;
		border-radius: 50%;
		background: linear-gradient(180deg, #f6f7fd 7%, #c8d9fa 102%);
		margin-right: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		.avatar {
			width: 26px;
			height: 25px;
		}
	}
	.basic-info {
		max-width: 196px;
		color: #fff;
		.company {
			font-size: 14px;
			font-weight: bold;
			line-height: 20px;
			margin-bottom: 4px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.name {
			font-size: 12px;
			font-weight: normal;
			line-height: 16px;
		}
	}
	.login-register {
		color: #fff;
		font-size: 14px;
		line-height: 20px;
	}

	.nav-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		& + .nav-item {
			margin-left: 10px;
		}

		.iconfont {
			font-size: 18px;
			position: relative;
		}
		.name {
			font-size: 11px;
			line-height: 16px;
			padding-top: 4px;
		}
	}
}

.quick-entry-box {
	height: 83px;
	border-radius: 5px;
	display: flex;
	gap: 16px;
	padding: 16px 8px;
	background: #fff;
	.entry-item {
		width: 74px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		> img {
			width: 30px;
			height: 30px;
		}
		.entry-name {
			padding-top: 4px;
			color: #333333;
			font-size: 12px;
			line-height: 16px;
		}
	}
}
.badge {
	position: absolute;
	right: -4px;
	top: 0;
	min-width: 6px;
	height: 6px;
	font-size: 12px;
	font-weight: 400;
	color: #ffffff;
	text-align: center;
	border-radius: 8px;
	&.danger {
		background: #ef3838;
	}
}
</style>
